import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '$icons/uncolored/Close.svg';
import { ReactComponent as CircleDot } from '$icons/CircleDot.svg';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';

interface ModalCandidateActionsProps {
  closeModal: () => void;
  handleOk: () => void;
  candidateFirstName?: string;
  candidateLastName?: string;
  lastJob: {
    title: string;
    company: string | null;
  };
  score: number;
  img?: string;
  type: 'interview' | 'reject' | 'shortlist' | 'info';
  className?: string;
  hideCloseButton?: boolean;
}

const ModalCandidateActions: React.FC<ModalCandidateActionsProps> = ({
  closeModal,
  handleOk,
  lastJob,
  score,
  img,
  type,
  candidateFirstName,
  candidateLastName,
  className,
  hideCloseButton,
}) => {
  const [t] = useTranslation('global');
  return (
    <div
      className={`flex flex-col gap-4 bg-white p-8 rounded-xl font-mulish md:min-w-[600px] ${className}`}
    >
      {!hideCloseButton && (
        <button
          type="button"
          className="text-spk-mediumContrast text-sm items-center flex flex-row justify-end ml-auto"
          onClick={() => closeModal()}
        >
          Close
          <CloseIcon className="stroke-spk-mediumContrast w-4 h-4" />
        </button>
      )}
      <div className="flex flex-col justify-between items-start gap-2 border-b-[1px] border-[#E6F0F4] mb-2">
        <h1 className="text-2xl font-extrabold">
          {type === 'interview' && t('modalCandidateActions.titleInterview')}
          {type === 'reject' && t('modalCandidateActions.titleReject')}
          {type === 'shortlist' && t('modalCandidateActions.titleShortlist')}
          {type === 'info' && t('modalCandidateActions.titleInfo')}
        </h1>
        {type === 'info' && (
          <p className="text-spk-mediumContrast">
            {t('modalCandidateActions.subTitleInfo')}
          </p>
        )}
      </div>
      <div className="items-center flex flex-col gap-8 w-full">
        <div className="flex justify-center sm:justify-between items-center w-full">
          <div className="flex gap-4 flex-col sm:flex-row items-center">
            <div className="w-[60px] h-[60px]">
              <DefaultIcon2
                picture={img}
                firstName={candidateFirstName}
                lastName={candidateLastName}
              />
            </div>
            <div className="flex flex-col gap-1 text-brand-darkgrey">
              <p className="font-semibold text-center sm:text-start">
                {`${candidateFirstName} ${candidateLastName}`}
              </p>
              <p className="font-xs text-center sm:text-start">
                {lastJob?.title || 'N/A'}
                {lastJob.company ? (
                  <>
                    <CircleDot className="inline-block align-middle mx-2 h-1 w-1" />
                    {lastJob.company}
                  </>
                ) : (
                  ''
                )}
              </p>
            </div>
          </div>
          <div className="hidden bg-tags-green10 text-tags-green20 px-3 py-2 rounded-xl gap-3 font-semibold items-center">
            <p className="hidden sm:block">Demand Score</p>
            <p className="text-2xl">{score}</p>
          </div>
        </div>
        <div className="flex gap-4 justify-end w-full flex-col-reverse sm:flex-row">
          {type !== 'info' && (
            <button
              type="button"
              className="flex text-employer-primary font-bold text-sm px-5 py-4 disabled:opacity-25 disabled:cursor-not-allowed"
              onClick={() => closeModal()}
            >
              {t('modalCandidateActions.notNow')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              handleOk();
              closeModal();
            }}
            className="flex text-spk-softBlue font-bold text-sm bg-employer-primary rounded-full px-5 py-4 disabled:opacity-25 disabled:cursor-not-allowed"
          >
            {type === 'interview' && t('modalCandidateActions.buttonInterview')}
            {type === 'reject' && t('modalCandidateActions.buttonReject')}
            {type === 'shortlist' && t('modalCandidateActions.buttonShortlist')}
            {type === 'info' && t('modalCandidateActions.buttonInfo')}
          </button>
        </div>
      </div>
    </div>
  );
};

ModalCandidateActions.defaultProps = {
  img: '',
  candidateFirstName: '',
  candidateLastName: '',
  className: '',
  hideCloseButton: false,
};

export default ModalCandidateActions;
